import type { ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import HomeView from './views/HomeView';
import EquityCalculatorView from './views/EquityCalculatorView';
import ReferenceLettersView from './views/ReferenceLettersView';
import ReferenceLetterRequestView from './views/ReferenceLetterRequestView';
import ManagerReviewRoute from './views/ManagerReviewRoute';
import ManagerReviewV2Page from './views/ManagerReviewV2Page';
import JobAutoCalculator from '../jobauto-calculator/JobAutoCalculator';
import JobAutoGuard from '../jobauto-calculator/JobAutoGuard';
import OnboardingSchedulingView from './views/OnboardingSchedulingView';
import TACockpitView from './views/TACockpitView';
import TalentPoolView from './views/TalentPoolView';
import TAActionBoardView from './views/TAActionBoardView';
import InterviewLinksView from './views/InterviewLinksView';
import CoraView from './views/cora/CoraView';
import InterviewsView from './views/InterviewsView';
import EmailLibraryView from './views/EmailLibraryView';
import OutreachView from './views/OutreachView';
import AbsenceInsightsView from './views/AbsenceInsightsView';
import PeopleCockpitView from './views/PeopleCockpitView';
import UsageAnalyticsView from './views/UsageAnalyticsView';
import TicketManagementView from './views/TicketManagementView';
import MyInquiriesView from './views/MyInquiriesView';
import SubmitRequestView from './views/SubmitRequestView';
import ChatWithBorisView from './views/ChatWithBorisView';
import InternalPlaygroundHiringTimelineView from './views/InternalPlaygroundHiringTimelineView';
import AopPlanningView from './views/AopPlanningView';
import RoleApprovalsView from './views/RoleApprovalsView';
import FeedbackTriageView from './views/FeedbackTriageView';
import SalaryBandsView from './views/SalaryBandsView';
import { RequirePermission } from '../../shared/components/RequirePermission';
import { CANDIDATE_COMMS_VIEW, INTERNAL_PLAYGROUND_VIEW, ONBOARDING_VIEW, ORG_PLANNING_OVERVIEW_ACCESS, REFERENCE_LETTERS_DRAFT } from './permissions';
import { Lock } from 'lucide-react';

/** The shell every "Access restricted" screen shares; each caller says what the page shows and who to ask. */
function AccessRestricted({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100">
          <Lock className="h-6 w-6 text-zinc-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Access restricted</h2>
          <p className="mt-1 text-sm text-zinc-500">{children}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Access screen for the Candidate Communication pages.
 *
 * Names the Workspace group deliberately: "ask an admin" leaves a colleague with
 * nowhere to go, while the group name is something they can put in a message.
 */
function CandidateCommunicationRestricted() {
  return (
    <AccessRestricted>
      The Candidate Communication pages show candidate data, so access is limited to the TA
      team. If you need it, ask to be added to the{' '}
      <span className="font-medium">People Space Candidate Communication</span> Google group
      (<span className="font-mono text-xs">cca.peoplespace@finn.com</span>).
    </AccessRestricted>
  );
}

/** Guarded parent for the Candidate Communication routes; renders them or the notice above. */
function CandidateCommunicationRoutes() {
  return (
    <RequirePermission
      strict
      permission={CANDIDATE_COMMS_VIEW}
      fallback={<CandidateCommunicationRestricted />}
    >
      <Outlet />
    </RequirePermission>
  );
}

/**
 * Access screen for People & Org Planning (Org Planning Overview + Role Approvals).
 *
 * Names the Workspace group, same reasoning as Candidate Communication's fallback: this now has a
 * real Google-Group-backed grant (`org-planning-viewers` / squad-org-planning@finn.com, wired
 * Sep 22 2026 — see rbac.js#RBAC_GROUP_ORG_PLANNING_VIEWERS), so "ask to be added to a group" is
 * the right instruction. Since Oct 19 2026 that group plus core admins is the whole audience: the
 * older per-user allowlist (rbac.js#INTERNAL_PLAYGROUND_ADMIN_EMAILS) is empty.
 */
function InternalPlaygroundRestricted() {
  return (
    <AccessRestricted>
      People &amp; Org Planning shows proposed headcount and approval decisions, so access is
      limited. If you need it, ask to be added to the{' '}
      <span className="font-medium">squad-org-planning</span> Google group (
      <span className="font-mono text-xs">squad-org-planning@finn.com</span>).
    </AccessRestricted>
  );
}

/** Access screen for Reference Letters: supervisors' gradings of named colleagues (Oct 2026). */
function ReferenceLettersRestricted() {
  return (
    <AccessRestricted>
      Reference Letters shows supervisors&apos; gradings of named colleagues, so access is limited to
      the People Business Partners (<span className="font-mono text-xs">people.business-partnering@finn.com</span>).
    </AccessRestricted>
  );
}

/**
 * Access screen for Onboarding Scheduling. The page lists incoming hires (PLA-626). The guard below
 * is `strict`, matching the proxy's always-enforced /api/onboarding gate, so this is what a colleague
 * without the permission sees. No Workspace group is named because none has been mapped yet — see
 * ONBOARDING_GOOGLE_GROUPS in proxy-server/lib/config.js.
 */
function OnboardingRestricted() {
  return (
    <AccessRestricted>
      Onboarding Scheduling shows incoming hires before their first day, so access is limited
      to the People team members who schedule their Get-to-Knows. If you need it, ask a People
      Space admin.
    </AccessRestricted>
  );
}

/**
 * Guarded parent for the Org Planning Overview; renders it or the notice above. Opens for BOTH
 * audiences — the full one and the proposers, whose limits the proxy enforces per request.
 */
function InternalPlaygroundRoutes() {
  return (
    <RequirePermission
      strict
      permission={ORG_PLANNING_OVERVIEW_ACCESS}
      fallback={<InternalPlaygroundRestricted />}
    >
      <Outlet />
    </RequirePermission>
  );
}

/** Role Approvals: the full Org Planning audience ONLY, never the proposers. Matches the proxy,
 * where `routes/role-approvals.js` authorizes `internal_playground.view` alone. */
function RoleApprovalsRoutes() {
  return (
    <RequirePermission strict permission={INTERNAL_PLAYGROUND_VIEW} fallback={<InternalPlaygroundRestricted />}>
      <Outlet />
    </RequirePermission>
  );
}

/** The People Space router: every view's route, with its permission guard where it has one. */
export default function PeopleSpace() {
  return (
    <Routes>
      <Route path="/" element={<HomeView />} />
      {/*
        One path per tab (and sub-tab), so a tab is linkable, survives a reload, and lands where
        a colleague meant when they paste a URL. The bare path keeps working for existing
        bookmarks — the view redirects it to the default tab canonically.
      */}
      <Route path="/ta-cockpit" element={<TACockpitView />} />
      <Route path="/ta-cockpit/:tab" element={<TACockpitView />} />
      <Route path="/ta-cockpit/:tab/:subtab" element={<TACockpitView />} />
      {/*
        CANDIDATE COMMUNICATION — one pathless guard around every page that shows candidate data,
        so the gate lives in one place instead of being re-added inside four views. `strict` because
        the matching proxy prefixes use `alwaysEnforce`: deferring to the audit-only flag here would
        render these pages for colleagues the API then answers 403 to.

        This is UI only. The proxy re-authorizes the same permission on every request — see the
        candidateComms() block in proxy-server/server.js.
      */}
      <Route element={<CandidateCommunicationRoutes />}>
        {/*
          One path per tab, so a tab is linkable, survives a reload, and lands where a colleague
          meant when they paste a URL. The bare path keeps working for existing bookmarks and
          resolves to the default tab, which the view redirects to canonically.
        */}
        <Route path="/ta-action-boards" element={<TAActionBoardView />} />
        <Route path="/ta-action-boards/:tab" element={<TAActionBoardView />} />
        <Route path="/interview-links" element={<InterviewLinksView />} />
        <Route path="/interviews" element={<InterviewsView />} />
        {/* Canonical path for the renamed page; /interviews stays for bookmarks. */}
        <Route path="/log" element={<InterviewsView />} />
        <Route path="/email-library" element={<EmailLibraryView />} />
        {/* Cora, the CV screening assistant: her verdicts are assessments of candidates' CVs. */}
        <Route path="/cora" element={<CoraView />} />
        <Route path="/cora/:tab" element={<CoraView />} />
        <Route path="/outreach" element={<OutreachView />} />
        {/* Talent Sourcing › Email Library: the same page, outreach InMail templates only. */}
        <Route path="/outreach-library" element={<EmailLibraryView variant="outreach" />} />
        {/*
          Feedback Triage moved here from the org-planning guard (Theresa, Sep 23 2026) together
          with its nav item. It is gated on the SAME permission as the boards the reports are
          about, which is the point: the people who file them are the people who work them. Note
          Usage Analytics, its neighbour in the Product Experience subgroup, deliberately stays
          OUTSIDE this guard — see its route below and its entry in config.ts.
        */}
        <Route path="/feedback-triage" element={<FeedbackTriageView />} />
        {/*
          Talent Community: inside the guard since the Vetted Talent import (Oct 21 2026) — real
          candidates' names, roles and CVs. Its data comes from /api/lever/talent-community/*,
          behind the same permission on the proxy.
        */}
        <Route path="/talent-pool" element={<TalentPoolView />} />
        <Route path="/talent-pool/:tab" element={<TalentPoolView />} />
      </Route>
      {/*
        Old path kept as a redirect so existing links/bookmarks still resolve. Deliberately OUTSIDE
        the guard: a redirect carries no candidate data, and gating it would answer a stale bookmark
        with "access restricted" instead of sending the person to the page that then decides.
      */}
      <Route path="/email-blocks" element={<Navigate to="/email-library" replace />} />
      {/* Outreach was a TA Action Board tab before it got its own page (Oct 13 2026). */}
      <Route path="/ta-action-boards/outreach" element={<Navigate to="/outreach" replace />} />
      <Route path="/equity-calculator" element={<EquityCalculatorView />} />
      <Route path="/salary-bands" element={<SalaryBandsView />} />
      <Route
        path="/reference-letters"
        element={
          // strict: the proxy ALWAYS enforces this permission, whatever RBAC_ENFORCEMENT_ENABLED says.
          <RequirePermission strict permission={REFERENCE_LETTERS_DRAFT} fallback={<ReferenceLettersRestricted />}>
            <ReferenceLettersView />
          </RequirePermission>
        }
      />
      {/* Open to everyone: the proxy decides whom each caller may request a letter for. */}
      <Route path="/request-reference-letter" element={<ReferenceLetterRequestView />} />
      {/* v1 + the pilot-only "try the new version" button; v2 is gated by the proxy (lib/managerReviewV2.js). */}
      <Route path="/manager-review" element={<ManagerReviewRoute />} />
      <Route path="/manager-review/v2" element={<ManagerReviewV2Page />} />
      {/*
        JobAuto and Onboarding Scheduling: both `strict` (JobAuto's inside JobAutoGuard), because
        the proxy enforces each permission with alwaysEnforce on /api/jobauto and /api/onboarding
        (PLA-626). Keep the two in step. UI only; the proxy is the real check.
      */}
      <Route path="/jobauto-calculator" element={<JobAutoGuard><JobAutoCalculator /></JobAutoGuard>} />
      <Route
        path="/onboarding-scheduling"
        element={
          <RequirePermission strict permission={ONBOARDING_VIEW} fallback={<OnboardingRestricted />}>
            <OnboardingSchedulingView />
          </RequirePermission>
        }
      />
      {/* Confidential — no nav entry; access-gated inside the view */}
      <Route path="/absence-insights" element={<AbsenceInsightsView />} />
      {/* Confidential — strict guard inside the view, matching the proxy's alwaysEnforce gate. */}
      <Route path="/people-cockpit" element={<PeopleCockpitView />} />
      {/*
        PEOPLE & ORG PLANNING — a pathless guard around Org Planning Overview + Role Approvals,
        same pattern as Candidate Communication above (see permissions.ts#INTERNAL_PLAYGROUND_VIEW).
        Route paths keep their historical `/internal-playground/...` and function names — only the
        sidebar section and display copy were renamed (Sep 23 2026), the permission is unchanged.
        It is granted by the org-planning-viewers Google Group plus core admins (the legacy per-user
        allowlist is empty since Oct 19 2026). This is UI only; the proxy re-authorizes the same
        permission (alwaysEnforce) on every request — see routes/internal-playground.js.
      */}
      <Route element={<InternalPlaygroundRoutes />}>
        {/*
          One path per tab, same convention as /ta-cockpit/:tab above — the bare path resolves to
          the Overview tab canonically (see the `Navigate replace` in the view itself).
        */}
        <Route
          path="/internal-playground/hiring-capacity-timeline"
          element={<InternalPlaygroundHiringTimelineView />}
        />
        <Route
          path="/internal-playground/hiring-capacity-timeline/:tab"
          element={<InternalPlaygroundHiringTimelineView />}
        />
      </Route>
      {/*
        Role Approvals: its own guard since Oct 1 2026, because the overview above now also admits
        the proposers and this page must not. A top-level path rather than a playground-nested one
        because the Slack announcement links straight here and that link outlives whatever the
        playground's slugs become.
      */}
      <Route element={<RoleApprovalsRoutes />}>
        <Route path="/role-approvals" element={<RoleApprovalsView />} />
      </Route>
      {/*
        AOP 2027 Planning (Anna, Oct 2026): department leads' drafts for next year. Its own page and
        its own proxy routes (/api/aop-planning), behind the same guard and audience as the Org
        Planning Overview — which it does not change.
      */}
      <Route element={<InternalPlaygroundRoutes />}>
        <Route path="/aop-2027" element={<AopPlanningView />} />
        <Route path="/aop-2027/:tab" element={<AopPlanningView />} />
      </Route>
      <Route path="/usage-analytics" element={<UsageAnalyticsView />} />
      <Route path="/ticket-management" element={<TicketManagementView />} />
      <Route path="/my-inquiries" element={<MyInquiriesView />} />
      <Route path="/submit-request" element={<SubmitRequestView />} />
      <Route path="/chat-with-boris" element={<ChatWithBorisView />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
